import en from '../locales/en.json'
import nl from '../locales/nl.json'
import sq from '../locales/sq.json'

// The frontend half of Rails i18n (docs/i18n.md): dictionaries are generated
// from config/locales/frontend/*.yml by `bin/rails i18n:export` and bundled —
// YAML stays the source of truth, and t() speaks Rails' own dialect
// (%{var} interpolation, `one`/`other` plural keys under a `count` lookup).
//
// The locale is set ONCE, in the Inertia entrypoint, from the server-shared
// `locale` prop before the first render — never flipped mid-session, so no
// re-render plumbing (a locale change arrives as a full server round trip).

type Dictionary = { [key: string]: string | Dictionary }

const DICTIONARIES: Record<string, Dictionary> = { en, nl, sq }
const FALLBACK_LOCALE = 'en'

/** The UI locales this build ships — exactly the bundled dictionaries. */
export const UI_LOCALES = Object.keys(DICTIONARIES)

let currentLocale = FALLBACK_LOCALE

/** Pick the active dictionary. Unknown locales fall back to English. */
export function setLocale(locale: string | undefined | null): void {
  currentLocale = locale && locale in DICTIONARIES ? locale : FALLBACK_LOCALE
}

export function getLocale(): string {
  return currentLocale
}

function lookup(dictionary: Dictionary, key: string): string | Dictionary | undefined {
  let node: string | Dictionary | undefined = dictionary
  for (const part of key.split('.')) {
    if (typeof node !== 'object' || node === undefined) return undefined
    node = node[part]
  }
  return node
}

/**
 * Translate a key, e.g. t('auth.sign_in.title') or
 * t('kid.coin_jar.price_label', { count: 3 }).
 *
 * Rails conventions, deliberately mirrored:
 * - `%{var}` placeholders, copy-identical with the YAML.
 * - A `count` variable resolves `one`/`other` subkeys (en, nl, and sq all
 *   share this plural rule) before interpolating.
 *
 * Divergence from Rails: a missing key or variable never raises — the key
 * (or placeholder) is returned so kid-land never crashes over a
 * translation. The YAML parity + freshness tests are the loud guard.
 */
export function t(key: string, vars: Record<string, string | number> = {}): string {
  let entry = lookup(DICTIONARIES[currentLocale], key) ?? lookup(DICTIONARIES[FALLBACK_LOCALE], key)

  if (typeof entry === 'object' && typeof vars.count === 'number') {
    entry = entry[vars.count === 1 ? 'one' : 'other']
  }

  if (typeof entry !== 'string') {
    if (import.meta.env?.MODE !== 'production') {
      console.warn(`[i18n] missing translation: ${currentLocale}.${key}`)
    }
    return key
  }

  return entry.replace(/%\{(\w+)\}/g, (placeholder, name: string) =>
    name in vars ? String(vars[name]) : placeholder
  )
}
